// utils/exportTableDOM.js
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const getTableData = (tableRef) => {
  let table = tableRef?.current;
  if (table && table.tagName !== "TABLE") {
    table = table.querySelector("table");
  }
  if (!table) {
    console.error("Export failed: no <table> element found on tableRef.");
    return { headers: [], rows: [] };
  }

  // Only elements whose nearest <table> ancestor is THIS table
  // (excludes anything inside a nested <table>, e.g. the expand/detail sub-tables)
  const belongsToThisTable = (el) => el.closest("table") === table;

  const headerCells = Array.from(table.querySelectorAll("th")).filter(
    belongsToThisTable
  );
  const skipIndexes = headerCells
    .map((th, i) => (th.dataset.export === "false" ? i : -1))
    .filter((i) => i !== -1);

  const headers = headerCells
    .map((th) => th.innerText.replace(/[↑↓⇅]/g, "").trim())
    .filter((_, i) => !skipIndexes.includes(i));

  // Data rows: belong to this table, have <td>, and are NOT the hidden expand/detail rows
  const allRows = Array.from(table.querySelectorAll("tr")).filter(
    (tr) =>
      belongsToThisTable(tr) &&
      tr.querySelectorAll("td").length > 0 &&
      !tr.classList.contains("expand")
  );

  const rows = allRows.map((tr) => {
    const cells = Array.from(tr.querySelectorAll("td")).filter(
      belongsToThisTable
    );
    return cells
      .map((td) => td.innerText.trim())
      .filter((_, i) => !skipIndexes.includes(i));
  });

  return { headers, rows };
};
export const exportToExcel = (tableRef, fileName = "export") => {
  const { headers, rows } = getTableData(tableRef);
  if (!headers.length) return;

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export const exportToCsv = (tableRef, fileName = "export") => {
  const { headers, rows } = getTableData(tableRef);
  if (!headers.length) return;

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const exportToPdf = (tableRef, fileName = "export") => {
  const { headers, rows } = getTableData(tableRef);
  if (!headers.length) return;

  const doc = new jsPDF({
    orientation: headers.length > 6 ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });

  autoTable(doc, {
    head: [headers],
    body: rows,
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    margin: { top: 40, left: 20, right: 20 },
    theme: "grid",
  });

  doc.save(`${fileName}.pdf`);
};